import {
  BarChart3,
  FileSignature,
  FolderOpen,
  Gavel,
  LayoutGrid,
  ListChecks,
  Gauge,
  ListX,
  Sparkles,
  Target,
} from 'lucide-react'
import { useEffect } from 'react'
import { useMe } from '@/auth/useMe'
import type { NavSection } from '@/layouts/navTypes'
import { WorkspaceLayout } from '@/layouts/WorkspaceLayout'
import { NAV_LABELS } from '@/lib/labels'
import { toast } from '@/lib/toast'

// Easter-egg: greet our intern Sebastiaan by name when he lands here. Keyed off a
// single hard-coded email so this is trivial to find and remove later. The
// "staigiar" spelling is a deliberate in-joke — do NOT "correct" it to "stagiair".
const SEBAS_EMAIL = 'sebastiaanvgroen@gmail.com'
const STAIGIAR_GREETED_KEY = 'vonk-staigiar-greeted'

/** Grouped sidebar nav (schets-style sections). "Mijn taken" and the profile
 *  block live at the bottom of the sidebar, outside these sections. */
const NAV_SECTIONS: readonly NavSection[] = [
  {
    title: 'Dealflow',
    items: [
      { to: '/preinvest/board', label: NAV_LABELS.board, icon: LayoutGrid },
      {
        to: '/preinvest/dossiers',
        label: 'Startup-dossier',
        icon: FolderOpen,
        // spec-64 U-100: an open dossier lives at /preinvest/deals/<id>.
        match: ['/preinvest/deals'],
      },
      {
        to: '/preinvest/analytics',
        label: 'Analyse & rapportage',
        icon: BarChart3,
        desktopOnly: true,
      },
    ],
  },
  {
    title: 'Beoordelen',
    items: [
      { to: '/preinvest/ic', label: 'IC-scores', icon: Gavel, desktopOnly: true },
      {
        to: '/preinvest/investeringsthese',
        label: 'Investeringsthese',
        icon: Target,
        adminOnly: true,
        desktopOnly: true,
      },
    ],
  },
  {
    title: 'Documenten',
    items: [
      { to: '/preinvest/ai-notulen', label: 'AI-notulen', icon: Sparkles, desktopOnly: true },
      { to: '/preinvest/nda', label: "NDA's", icon: FileSignature, desktopOnly: true },
    ],
  },
  {
    title: 'Beheer',
    items: [
      // spec-60: the one list the team picks from when rejecting (admin-managed).
      {
        to: '/preinvest/afwijsredenen',
        label: 'Afwijsredenen',
        icon: ListX,
        adminOnly: true,
      },
      // spec-78: this month's Claude cost against the budget.
      { to: '/preinvest/ai-verbruik', label: 'AI-verbruik', icon: Gauge, adminOnly: true },
    ],
  },
] as const

const TASKS_ITEM = { to: '/preinvest/taken', label: NAV_LABELS.myTasks, icon: ListChecks } as const

/**
 * Persistent pre-invest shell. The chrome (sidebar on desktop, topbar + drawer
 * on mobile) lives in WorkspaceLayout; this component only supplies the nav and
 * keeps the easter-egg below.
 */
export function PreinvestLayout() {
  const { me } = useMe()

  // Easter-egg: fire the intern's greeting once per browser session. The
  // sessionStorage guard means it survives PreinvestLayout remounts (useMe is
  // module-cached) and never repeats on route changes. Remove alongside
  // SEBAS_EMAIL when the joke has run its course.
  useEffect(() => {
    if (me?.email !== SEBAS_EMAIL) return
    if (sessionStorage.getItem(STAIGIAR_GREETED_KEY)) return
    sessionStorage.setItem(STAIGIAR_GREETED_KEY, '1')
    toast('Hey staigiar', { description: 'Welkom terug in de pre-invest workspace.' })
  }, [me?.email])

  return <WorkspaceLayout navSections={NAV_SECTIONS} footerItem={TASKS_ITEM} />
}
